
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { Search } from "../../assets/icons/icons";
import { useTranslation } from "react-i18next";

const API_BASE = "https://laravel-api.aj-mart.com";

const buildStorageUrl = (path = "") => {
  const value = String(path || "").trim();

  if (!value) return "";

  if (/^https?:\/\//i.test(value)) return value;

  return `${API_BASE}/storage/app/public/${value.replace(/^\/+/, "")}`;
};

const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/ـ/g, "")
    .replace(/\s+/g, " ")
    .trim();

const getList = (data, key) => {
  const candidates = [
    data?.[key],
    data?.data?.[key],
    data?.data,
    data,
  ];

  return candidates.find(Array.isArray) || [];
};

const normalizeItem = (item, type) => {
  const accessory = type === "accessory";
  const prefix = accessory ? "accessories" : "product";

  const id = item?.[`${prefix}_id`];

  if (id === undefined || id === null) return null;

  const nameAr = item?.[`${prefix}_name_A`] || "";
  const nameEn = item?.[`${prefix}_name_E`] || "";

  const brand = item?.[`${prefix}_brand_name`] || "";
  const category = item?.[`${prefix}_category`] || "";
  const tag = item?.[`${prefix}_tag`] || "";

  const searchFields = [
    nameAr,
    nameEn,
    brand,
    category,
    tag,
    item?.[`${prefix}_desc_A`],
    item?.[`${prefix}_desc_E`],
    item?.[`${prefix}_features_A`],
    item?.[`${prefix}_features_E`],
    item?.product_sub_category,
    item?.accessories_model,
  ];

  const price = Number(item?.[`${prefix}_price`] || 0);
  const discount = Number(
    item?.[`${prefix}_price_discount`] || 0
  );

  return {
    id,
    type,
    nameAr,
    nameEn,
    brand,
    category,
    image: buildStorageUrl(item?.[`${prefix}_main_img`]),
    price,
    discount,
    finalPrice:
      discount > 0
        ? price * (1 - discount / 100)
        : price,
    searchText: normalizeText(searchFields.join(" ")),
    nameSearch: normalizeText(`${nameAr} ${nameEn}`),
    brandSearch: normalizeText(brand),
  };
};

export default function SearchSection({ desktop = false }) {
  const { t, i18n } = useTranslation();

  const isArabic = i18n.language?.startsWith("ar");

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const desktopRef = useRef(null);

  const tr = (ar, en) => (isArabic ? ar : en);

  useEffect(() => {
    let active = true;

    const fetchAll = async () => {
      setLoading(true);
      setError("");

      const results = await Promise.allSettled([
        axios.get(`${API_BASE}/api/products`),
        axios.get(`${API_BASE}/api/accessories`),
      ]);

      if (!active) return;

      const productResult = results[0];
      const accessoryResult = results[1];

      const productsOk = productResult.status === "fulfilled";
      const accessoriesOk = accessoryResult.status === "fulfilled";

      if (productsOk) {
        setProducts(
          getList(productResult.value.data, "products")
        );
      } else {
        console.error("Products search error:", productResult.reason);
        setProducts([]);
      }

      if (accessoriesOk) {
        setAccessories(
          getList(accessoryResult.value.data, "accessories")
        );
      } else {
        console.error(
          "Accessories search error:",
          accessoryResult.reason
        );
        setAccessories([]);
      }

      if (!productsOk && !accessoriesOk) {
        setError(
          tr(
            "تعذر تحميل المنتجات والإكسسوارات",
            "Failed to load products and accessories"
          )
        );
      } else if (!productsOk || !accessoriesOk) {
        setError(
          tr(
            "تعذر تحميل بعض نتائج البحث",
            "Some search results could not be loaded"
          )
        );
      }

      setLoading(false);
    };

    fetchAll();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!desktop) return;

    const handleOutside = (event) => {
      if (
        desktopRef.current &&
        !desktopRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [desktop]);

  useEffect(() => {
    if (!isOpen || desktop) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") closeSearch();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, desktop]);

  const allItems = useMemo(() => {
    const normalizedProducts = products
      .map((item) => normalizeItem(item, "product"))
      .filter(Boolean);

    const normalizedAccessories = accessories
      .map((item) => normalizeItem(item, "accessory"))
      .filter(Boolean);

    return [...normalizedProducts, ...normalizedAccessories];
  }, [products, accessories]);

  const filteredItems = useMemo(() => {
    const term = normalizeText(searchTerm);

    if (!term) return [];

    const words = term.split(" ").filter(Boolean);

    return allItems
      .filter((item) =>
        words.every((word) => item.searchText.includes(word))
      )
      .map((item) => {
        let score = 0;

        if (item.nameSearch === term) score += 100;
        if (item.nameSearch.startsWith(term)) score += 60;
        if (item.nameSearch.includes(term)) score += 40;
        if (item.brandSearch.includes(term)) score += 15;

        return { ...item, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [allItems, searchTerm]);

  const getItemName = (item) =>
    isArabic
      ? item.nameAr || item.nameEn || "منتج"
      : item.nameEn || item.nameAr || "Product";

  const getItemLink = (item) =>
    item.type === "accessory"
      ? `/accessoriesdetails/${item.id}`
      : `/productdetails/${item.id}`;

  const closeSearch = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const renderResults = () => {
    if (!searchTerm.trim()) {
      return (
        <div className="py-6 text-center text-gray-400 text-sm">
          {tr(
            "ابدأ بكتابة اسم المنتج أو الإكسسوار",
            "Start typing a product or accessory name"
          )}
        </div>
      );
    }

    if (loading) {
      return (
        <div className="py-6 text-center text-gray-500 text-sm">
          {tr("جاري تحميل النتائج...", "Loading results...")}
        </div>
      );
    }

    return (
      <>
        {error && (
          <div className="p-3 text-center text-amber-600 text-xs">
            {error}
          </div>
        )}

        {!filteredItems.length && (
          <div className="py-8 text-center text-gray-500 text-sm">
            {tr(
              "لا توجد منتجات أو إكسسوارات مطابقة",
              "No matching products or accessories found"
            )}
          </div>
        )}

        {filteredItems.map((item) => {
          const name = getItemName(item);

          return (
            <Link
              key={`${item.type}-${item.id}`}
              to={getItemLink(item)}
              onClick={closeSearch}
              className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 hover:bg-gray-50 transition"
              dir={isArabic ? "rtl" : "ltr"}
            >
              <div className="w-[60px] h-[60px] border rounded-md bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={name}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-400 text-xs">
                    {tr("بدون صورة", "No Image")}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={`text-[10px] px-2 py-[2px] rounded-full font-medium ${
                      item.type === "accessory"
                        ? "bg-purple-50 text-purple-700"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    {item.type === "accessory"
                      ? tr("إكسسوار", "Accessory")
                      : tr("منتج", "Product")}
                  </span>

                  {item.brand && (
                    <span className="text-[11px] text-gray-400">
                      {item.brand}
                    </span>
                  )}
                </div>

                <div className="font-semibold text-[14px] text-gray-800 line-clamp-2">
                  {name}
                </div>

                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-bold text-[#1485a6] text-sm">
                    {item.finalPrice.toFixed(2)} {t("JOD")}
                  </span>

                  {item.discount > 0 && (
                    <span className="line-through text-gray-400 text-xs">
                      {item.price.toFixed(2)} {t("JOD")}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </>
    );
  };

  if (desktop) {
    return (
      <div ref={desktopRef} className="relative w-[450px] max-w-full">
        <div className="absolute left-[12px] top-1/2 -translate-y-1/2 z-10 pointer-events-none">
          <Search />
        </div>

        <input
          type="search"
          placeholder={tr(
            "ابحث عن منتج أو إكسسوار...",
            "Search products or accessories..."
          )}
          value={searchTerm}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
          className="rounded-[7px] w-full py-[9px] px-[40px] bg-[#f5f5f5] border-0 outline-0 shadow-[0px_0px_10px_#00000024]"
        />

        {isOpen && searchTerm.trim() && (
          <div
            className="absolute top-[48px] left-0 w-full bg-white rounded-[8px] shadow-xl border border-gray-100 z-[9999] max-h-[420px] overflow-y-auto"
          >
            {renderResults()}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="w-fit cursor-pointer"
        onClick={() => setIsOpen(true)}
        aria-label={tr("بحث", "Search")}
      >
        <Search />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-start pt-[100px] z-[9999]"
          onClick={closeSearch}
        >
          <div
            className="bg-white rounded-[10px] p-[20px] w-[90%] max-w-[500px] shadow-lg max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            dir={isArabic ? "rtl" : "ltr"}
          >
            <div className="flex justify-between items-center mb-[15px]">
              <h3 className="text-lg font-bold">
                {tr("البحث", "Search")}
              </h3>

              <button
                type="button"
                onClick={closeSearch}
                className="text-gray-500 hover:text-gray-700 text-xl"
                aria-label={tr("إغلاق", "Close")}
              >
                ✕
              </button>
            </div>

            <input
              type="search"
              placeholder={tr(
                "ابحث عن منتج أو إكسسوار...",
                "Search products or accessories..."
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              className="rounded-[7px] w-full py-[9px] px-[15px] bg-[#f5f5f5] border border-gray-300 outline-0 shadow-[0px_0px_10px_#00000024]"
            />

            <div className="mt-4 overflow-y-auto flex-1 min-h-0">
              {renderResults()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
